import { Button, Label, Modal, RangeSlider, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import pluralize from "pluralize";
import React, { useState } from "react";
import { Channel, ChannelType, ChannelTypeCopy } from "types";
import { QuantitySpinner } from "../../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../../Common/SecondaryButton";
import "./WaiverReminderPreference.css";
import { NotificationPreferenceListItem } from "./useNotificationPreferenceList";
import { NoTranslate } from "@/components/Common/NoTranslate";

interface WaiverReminderPreferenceModalProps {
    item: NotificationPreferenceListItem;
    onApply: (value: Channel[]) => void;
}

export const WaiverReminderPreferenceModal = ({ item, onApply }: WaiverReminderPreferenceModalProps) => {
    const [isPreferenceModalOpen, setIsPreferenceModalOpen] = useState(false);
    const isEmailChannelEnabled = isChannelEnabled(item.channels, ChannelType.EMAIL);
    const isSMSChannelEnabled = isChannelEnabled(item.channels, ChannelType.SMS);

    let emailChannels = getChannels(item.channels, ChannelType.EMAIL);
    const smsChannels = getChannels(item.channels, ChannelType.SMS);
    if (emailChannels.length === 1 && emailChannels[0]?.offset === "") {
        // If default emailChannels and no settings preference added, get Default email channels
        emailChannels = getDefaultChannels(isEmailChannelEnabled, ChannelType.EMAIL, smsChannels);
    }

    const daysAfterPurchase = emailChannels.filter((channel) => channel.negativeOffset);
    const daysBeforeEvent = emailChannels.filter((channel) => !channel.negativeOffset);

    const applyAndClose = (daysSelected: number[]) => {
        const emailChannelConfigs = getChannelConfig(daysSelected, ChannelType.EMAIL, isEmailChannelEnabled);
        const smsChannelConfigs = getChannelConfig(daysSelected, ChannelType.SMS, isSMSChannelEnabled);
        onApply([...emailChannelConfigs, ...smsChannelConfigs]);
        setIsPreferenceModalOpen(false);
    };

    return (
        <>
            Sent to your customers
            {daysAfterPurchase.length > 0 && (
                <>
                    <DaysTextContent
                        channels={daysAfterPurchase}
                        onClick={() => {
                            setIsPreferenceModalOpen(true);
                        }}
                    />
                    after purchase date {daysBeforeEvent.length > 0 && "and"}
                </>
            )}
            {daysBeforeEvent.length > 0 && (
                <>
                    <DaysTextContent
                        channels={daysBeforeEvent}
                        onClick={() => {
                            setIsPreferenceModalOpen(true);
                        }}
                    />
                    before event start date
                </>
            )}
            <Modal
                isOpen={isPreferenceModalOpen}
                onClose={() => {
                    setIsPreferenceModalOpen(false);
                }}
            >
                <ModalContent
                    numberOfReminders={emailChannels.length}
                    emailChannels={emailChannels}
                    onApply={applyAndClose}
                    onClose={() => {
                        setIsPreferenceModalOpen(false);
                    }}
                />
            </Modal>
        </>
    );
};

interface ModalContentProps {
    numberOfReminders: number;
    emailChannels: Channel[];
    onApply: (values: any) => void;
    onClose: () => void;
}

const ModalContent = ({ onApply, numberOfReminders, emailChannels, onClose }: ModalContentProps) => {
    const [reminderCount, setReminderCount] = useState(numberOfReminders);
    const maximumReminders = 3;

    const startValues = emailChannels
        .map((channel) => {
            const offset = dayjs.duration(channel.offset || "PT0M").asDays();
            return channel.negativeOffset ? offset - 31 : 30 - offset;
        })
        .sort((previous, next) => previous - next);
    const [dayValues, setDayValues] = useState(startValues);

    const tooltipCustomFormatter = dayValues.map(() => {
        return {
            from: (value: number) => value,
            to: (value: number) => {
                value = Math.round(value);
                if (value < 0) {
                    value += 31;
                } else {
                    value = 30 - value;
                }

                return `${value} ${pluralize("day", value)}`;
            },
        };
    });

    const formatAndSetDayValues = (value: string[]) => {
        const newDayValues = value.map(Number);
        setDayValues(newDayValues);
    };

    const handleRemainderCountChange = (value: number) => {
        const difference = value - reminderCount;
        const newDayValues = [...dayValues];
        if (difference === -1) {
            newDayValues.pop();
        } else if (difference === 1) {
            let nextDay = dayjs.duration(DefaultOffset.OFFSET_DEFAULT_THIRD).asDays();
            if (dayValues.length === 1) {
                nextDay = dayjs.duration(DefaultOffset.OFFSET_DEFAULT_SECOND).asDays();
            } else if (dayValues.length === 2) {
                nextDay = dayjs.duration(DefaultOffset.OFFSET_DEFAULT_FIRST).asDays();
            }

            nextDay = 30 - nextDay;
            newDayValues.push(nextDay);
        }

        newDayValues.sort((previous, next) => previous - next);
        setDayValues(newDayValues);
        setReminderCount(value);
    };

    return (
        <>
            <Modal.Header>Waiver Reminder</Modal.Header>

            <Modal.Body className="flex-col">
                <div className="flex flex-row items-center">
                    <QuantitySpinner
                        classNames={{ fieldset: "mr-2" }}
                        value={reminderCount}
                        min={1}
                        max={maximumReminders}
                        onChange={handleRemainderCountChange}
                    />
                    <div className="font-bold">Number of reminders</div>
                </div>

                <div className="mt-3 flex flex-row justify-between">
                    <Label>Days After Purchase</Label>
                    <Label>Days Before Event Date</Label>
                </div>

                <div
                    className={clsx(
                        "waiver-reminder-slider mb-20 flex flex-row",
                        dayValues.length > 1 ? "mt-12" : "mt-2",
                    )}
                >
                    <RangeSlider
                        key={dayValues.length}
                        values={dayValues}
                        min={-31}
                        max={30}
                        tooltipCustomFormatter={tooltipCustomFormatter}
                        onChange={formatAndSetDayValues}
                    />
                    {/* Moves this <hr > tags to absolute 42% of range slider length, to give a divider look. also range slider is not uniformly divided so 42% works */}
                    <hr className="absolute -mt-1 ml-[42%] h-4 border-l-2 border-solid border-black" />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <Button
                    onClick={() => {
                        onApply(dayValues);
                    }}
                >
                    Apply
                </Button>
            </Modal.Footer>
        </>
    );
};

enum DefaultOffset {
    OFFSET_DEFAULT_THIRD = "P7D",
    OFFSET_DEFAULT_SECOND = "P3D",
    OFFSET_DEFAULT_FIRST = "P1D",
}

const getDefaultChannels = (isChannelEnabled: boolean, channelType: ChannelType, otherChannels: Channel[]) => {
    // If other channel configured already, use that negativeOffset and offset
    if (otherChannels.length > 0 && otherChannels[0]?.offset !== "") {
        return otherChannels.map((channel) => {
            return {
                type: channelType,
                negativeOffset: channel.negativeOffset,
                offset: channel.offset,
                enabled: isChannelEnabled,
            };
        });
    }

    if (otherChannels.length === 0) {
        return [
            {
                type: channelType,
                negativeOffset: true,
                offset: "",
                enabled: isChannelEnabled,
            },
        ];
    }

    return Object.keys(DefaultOffset).map((key) => {
        return {
            type: channelType,
            negativeOffset: false,
            offset: DefaultOffset[key as keyof typeof DefaultOffset],
            enabled: isChannelEnabled,
        };
    });
};

const getDaysText = (channels: Channel[]) => {
    const { length } = channels;
    const elements = channels.map((channel, index) => {
        const offset = dayjs.duration(channel.offset || "PT0M").asDays();
        const element = (
            <span>
                <NoTranslate>{offset} </NoTranslate>
                <span>{pluralize("day", offset)}</span>
            </span>
        );
        if (index < length - 1) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                    {element}
                    <span>, </span>
                </React.Fragment>
            );
        }

        return element;
    });
    return elements;
};

interface DaysTextContentProps {
    channels: Channel[];
    onClick: () => void;
}

const DaysTextContent = ({ channels, onClick }: DaysTextContentProps) => {
    const filteredChannels = channels.filter((channel) => {
        return channel.offset === "P0D";
    });
    if (filteredChannels.length === 3) {
        channels.pop();
    }

    // Default can be ‘Send to your customers 7 days, 3 days and 1 day before event start date’
    return (
        <span className="mx-1 cursor-pointer border-b border-primary font-bold text-blue" onClick={onClick}>
            {channels.length > 0 ? getDaysText(channels) : ""}
        </span>
    );
};

interface WaiverReminderChannelsPreferenceProps {
    item: NotificationPreferenceListItem;
    onApply: (value: Channel[]) => void;
}

export const WaiverReminderChannelsPreference = ({ item, onApply }: WaiverReminderChannelsPreferenceProps) => {
    const isEmailChannelEnabled = isChannelEnabled(item.channels, ChannelType.EMAIL);
    const isSMSChannelEnabled = isChannelEnabled(item.channels, ChannelType.SMS);

    let emailChannels = getChannels(item.channels, ChannelType.EMAIL);
    let smsChannels = getChannels(item.channels, ChannelType.SMS);

    if (emailChannels.length === 1 && emailChannels[0].offset === "") {
        emailChannels = getDefaultChannels(isEmailChannelEnabled, ChannelType.EMAIL, smsChannels);
    }

    if (smsChannels.length === 1 && smsChannels[0].offset === "") {
        smsChannels = getDefaultChannels(isSMSChannelEnabled, ChannelType.SMS, emailChannels);
    }

    const enableEmailChannel = (isChecked: boolean) => {
        emailChannels = emailChannels.map((channel) => {
            return { ...channel, enabled: isChecked };
        });
        onApply([...emailChannels, ...smsChannels]);
    };

    const enableSMSChannel = (isChecked: boolean) => {
        smsChannels = smsChannels.map((channel) => {
            return { ...channel, enabled: isChecked };
        });
        onApply([...emailChannels, ...smsChannels]);
    };

    return (
        <div className="space-y-1">
            <div key={ChannelType.SMS} className="flex items-center">
                <Switch.Group>
                    <Switch isChecked={isSMSChannelEnabled} size="medium" onChange={enableSMSChannel} />
                    <Switch.Label direction="left">
                        <span className="pl-2 text-sm text-gray-darker">{ChannelTypeCopy[ChannelType.SMS]}</span>
                    </Switch.Label>
                </Switch.Group>
            </div>
            <div key={ChannelType.EMAIL} className="flex items-center">
                <Switch.Group>
                    <Switch isChecked={isEmailChannelEnabled} size="medium" onChange={enableEmailChannel} />
                    <Switch.Label direction="left">
                        <span className="pl-2 text-sm text-gray-darker">{ChannelTypeCopy[ChannelType.EMAIL]}</span>
                    </Switch.Label>
                </Switch.Group>
            </div>
        </div>
    );
};

const isChannelEnabled = (channels: Channel[], channelType: ChannelType): boolean => {
    return channels.some((channel) => channel.type === channelType && channel.enabled);
};

const getChannels = (channels: Channel[], channelType: ChannelType): Channel[] => {
    return channels.filter((channel) => channel.type === channelType);
};

const getChannelConfig = (daysSelected: number[], channelType: ChannelType, isEnabled: boolean): Channel[] => {
    return daysSelected.map((day) => {
        let value = Math.round(day);
        const negativeOffset = value < 0;
        if (value < 0) {
            value += 31;
        } else {
            value = 30 - value;
        }

        return {
            type: channelType,
            negativeOffset,
            offset: `P${value}D`,
            enabled: isEnabled,
        };
    });
};
